import { Grid } from '../../../../../Common/Grid'
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { URLS } from '../../../../constants'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../../utils/api'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useFormik } from 'formik'
import { getValidationSchema, initialValues } from './helpers'
import { useMemo, useState } from 'react'
import { useNotification } from '../../../../../../../hooks'
import {
  CircularProgress,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper
} from '@mui/material'
import ImageUpload from '../../../../Common/ImageUpload/ImageUpload'
import AlertModal from '../../../../Common/AlertModal/AlertModal'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import React from "react";

const ExpectedReturnDetailStep3 = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const [isInvalidTruckNumberModalOpen, setIsInvalidTruckNumberModalOpen] = useState(false)
  const [InvalidTruckNumberModalContent, setInvalidTruckNumberModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [initialSupportList, setInitialSupportList] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [params] = useSearchParams()
  const truckId = Number(params.get('truckId'))
  const parentId = Number(params.get('parentId'))

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        let message = 'Există diferențe între cantitatea aşteptată şi cea descărcată pentru '
        let difference = false
        let hasAllPhotos = true

        values.supportList.forEach((support: any) => {
          if (support.confirmedSupportNumber !== support.supportNumber) {
            message += support.supportTypeName + ', '
            difference = true
            if (!support.hasPhoto) hasAllPhotos = false
          }
        })

        if (!hasAllPhotos) {
          message = message.substring(0, message.length - 2) + '! '
          message += 'Atasaţi imagini doveditoare! '
          await openInvalidTruckNumberModal(message, false)
        } else if (difference) {
          message = message.substring(0, message.length - 2) + '! '
          await openInvalidTruckNumberModal(message, true)
        } else {
          await onContinueSubmit()
        }
      } catch (error) {
        showNotification({
          message: `Error while submitting. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const { data: truck, isLoading } = useQuery(
    'RDsupportList',
    async () => {
      const truck = await makeCoreGetRequest(
        '/shop-reception-mobile/truck/hub/exit/supports/' + truckId + '?parentId=' + parentId,
        null
      )
      await formik.setFieldValue('supportList', truck.supportList)
      setInitialSupportList(truck.supportList)
      return truck
    },
    {
      initialData: [],
      onError: error => {
        showNotification({
          message: `Error while fetching truck. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: allSupportList, isLoading: isSupportListLoading } = useQuery(
    'RDallSupportList',
    async () => {
      return await makeCoreGetRequest(
        '/shop-reception-mobile/truck/hub/exit/all-supports/' + truckId,
        null
      )
    },
    {
      initialData: [],
      onError: error => {
        showNotification({
          message: `Error while fetching truck. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const onContinueSubmit = async () => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      const supportList =
        formik.values.supportList?.map((support: any) => ({
          id: support.id,
          supportCode: support.supportCode,
          supportTypeName: support.supportTypeName,
          supportNumber: support.supportNumber,
          confirmedSupportNumber: support.confirmedSupportNumber,
          supportTypeNameAndCode: support.supportTypeNameAndCode,
          hasPhoto: support.hasPhoto
        })) || []

      await makeCorePostRequest('/shop-reception-mobile/truck/hub/exit/confirm-supports', {
        truckId,
        supportList
      })
      navigate(`${URLS.AUTH_EXPECTED_RETURN_DETAIL_FINAL}?id=${truckId}`)
    } catch (error) {
      showNotification({
        message: `Error while saving supports. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const openInvalidTruckNumberModal = async (message, showTerminatButton) => {
    showTerminatButton
      ? setInvalidTruckNumberModalContent({
          title: 'Atenție!',
          description: message,
          buttons: [
            {
              label: 'Am terminat',
              onClick: async () => {
                await onContinueSubmit()
                setIsInvalidTruckNumberModalOpen(false)
              },
              color: 'primary',
              disabled: isSubmitting
            },
            {
              label: 'Mai verific',
              onClick: () => setIsInvalidTruckNumberModalOpen(false),
              color: 'primary'
            }
          ]
        })
      : setInvalidTruckNumberModalContent({
          title: 'Atenție!',
          description: message,
          buttons: [
            {
              label: 'Mai verific',
              onClick: () => setIsInvalidTruckNumberModalOpen(false),
              color: 'primary'
            }
          ]
        })
    setIsInvalidTruckNumberModalOpen(true)
  }

  const closeInvalidTruckNumberModal = () => {
    setIsInvalidTruckNumberModalOpen(false)
  }

  if (isLoading || isSupportListLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const handleSuccess = id => {
    showNotification({
      message: 'Imaginea a fost încărcată cu succes.',
      type: 'success'
    })
    formik.setFieldValue(`supportList[${id}].hasPhoto`, true)
  }

  const handleError = error => {
    showNotification({
      message: `Error uploading image: ${error.message}`,
      type: 'error'
    })
  }

  const rows = formik.values.supportList?.map((support, index) => ({
    id: index,
    supportCode: support.supportCode,
    supportTypeName: support.supportTypeName,
    confirmedSupportNumber: support.confirmedSupportNumber,
    hasPhoto: support.hasPhoto
  }))

  const isDisabled = () => {
    if (truck.stackNumber === 0) return false

    if (formik.values.supportList) {
      for (const support of formik.values.supportList) {
        if (support.confirmedSupportNumber > 0) {
          return false
        }
      }
    }

    return true
  }

  const handleSelectChange = value => {
    const initialSupport = initialSupportList.find(initialSupport => initialSupport.id === value)

    const newSupport = initialSupport ?? {
      ...allSupportList.find(support => support.id === value),
      id: 0
    }

    formik.setFieldValue('supportList', [...formik.values.supportList, newSupport])
  }

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title="RETURURI AȘTEPTATE CAMION" />,
        <>
          {truck?.truckNumber && (
            <Typography variant="medium" component="p">
              {truck?.truckNumber} / {truck?.driverName}
            </Typography>
          )}
          {truck.shopNameAndCode && (
            <Typography variant="medium" component="p">
              {truck.shopNameAndCode} - numar aviz {truck.noticeNumber}
            </Typography>
          )}
        </>,
        <Typography variant="medium" component="h4">
          Recepție retur
        </Typography>,
        <TableContainer component={Paper}>
          <Table>
            <TableHead style={{ background: '#0970e6' }}>
              <TableRow>
                <TableCell align="left"></TableCell>
                <TableCell align="left"></TableCell>
                <TableCell
                  align="center"
                  style={{ fontFamily: 'Ubuntu', color: '#f8f9fb', fontWeight: 'bold' }}
                >
                  CANTITATE
                </TableCell>
                <TableCell align="left"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <React.Fragment key={row.id}>
                  <TableRow>
                    <TableCell align="left">{row.supportCode}</TableCell>
                    <TableCell align="left">{row.supportTypeName}</TableCell>
                    <TableCell align="center">
                      <Input
                        type="number"
                        value={row.confirmedSupportNumber}
                        onChange={e => {
                          formik.setFieldValue(
                            `supportList[${row.id}].confirmedSupportNumber`,
                            +(e.target as HTMLInputElement).value
                          )
                        }}
                        variant="standard"
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={4}>
                      <ImageUpload
                        uploadUrl={`/shop-reception-mobile/truck/hub/exit/upload-support/${truckId}/${
                          formik.values.supportList[row.id].id
                        }?supportCode=${row.supportCode}`}
                        onSuccess={() => handleSuccess(row.id)}
                        onError={handleError}
                        btnStyle={{ color: row.hasPhoto ? 'green' : '#0970E6' }}
                      />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
        <AsyncAutocomplete
          variant="outlined"
          name="searchSupportType"
          label="Adaugă ambalaj"
          options={allSupportList?.map(support => ({
            value: support.id,
            label: `${support.supportCode} - ${support.supportTypeName}`
          }))}
          onChange={support => handleSelectChange(support?.value)}
          error={!!formik.errors.supportType}
          helperText={formik.errors.supportType as string}
          disableClearable
        />,
        <Button
          style={{ width: '100%' }}
          type="button"
          variant="contained"
          label="SUMAR CAMION"
          onClick={() => {
            formik.handleSubmit(formik.values.supportList)
          }}
          disabled={isDisabled() || isSubmitting}
        />,
        <AlertModal
          open={isInvalidTruckNumberModalOpen}
          handleClose={closeInvalidTruckNumberModal}
          title={InvalidTruckNumberModalContent.title}
          description={InvalidTruckNumberModalContent.description}
          buttons={InvalidTruckNumberModalContent.buttons}
        />
      ]}
      itemsPerRow={1}
    />
  )
}

export default ExpectedReturnDetailStep3
